/**
 * DemocratCharts — formatting helpers.
 *
 * Trader-friendly number formatting:
 *   - Prices: 2 decimals usually, more for forex/crypto if needed
 *   - Volume: K/M/B suffixes
 *   - Percent: signed, fixed decimal
 */

export function formatPrice(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatVolume(value: number): string {
  if (!Number.isFinite(value) || value === 0) return "—";
  const abs = Math.abs(value);
  if (abs >= 1e9) return (value / 1e9).toFixed(2) + "B";
  if (abs >= 1e6) return (value / 1e6).toFixed(2) + "M";
  if (abs >= 1e3) return (value / 1e3).toFixed(1) + "K";
  return value.toString();
}

export function formatPercent(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return "—";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(decimals)}%`;
}

export function formatDate(iso: string | Date, includeTime = false): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "—";
  const opts: Intl.DateTimeFormatOptions = includeTime
    ? { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }
    : { year: "numeric", month: "short", day: "2-digit" };
  return d.toLocaleDateString("en-US", opts);
}

/** Lightweight Charts expects time as either a YYYY-MM-DD string or a unix ts in seconds. */
export function tsToChartTime(iso: string): number {
  return Math.floor(new Date(iso).getTime() / 1000);
}
